import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useState } from 'react';
import { useSiteConfig } from '../contexts/SiteConfigProvider';
import { Art } from '../types';

const getDescription = (art: Art, tone: string): string | undefined => {
    if (art.descriptions) {
        return art.descriptions[tone] ?? art.descriptions['mental-health-students'];
    }
    return art.description;
};

interface ArtDetailModalProps {
    art: Art | null;
    artList: Art[];
    onClose: () => void;
    onNavigate: (id: number) => void;
}

const ArtDetailModal: React.FC<ArtDetailModalProps> = ({ art, artList, onClose, onNavigate }) => {
    const artIndex = art ? artList.findIndex((a) => a.id === art.id) : -1;
    const hasPrev = artIndex > 0;
    const hasNext = artIndex < artList.length - 1;
    const [direction, setDirection] = useState(1);
    const { activeTone } = useSiteConfig();

    const navigatePrev = () => {
        setDirection(-1);
        onNavigate(artList[artIndex - 1].id);
    };

    const navigateNext = () => {
        setDirection(1);
        onNavigate(artList[artIndex + 1].id);
    };

    return (
        <Modal
            open={!!art}
            onClose={onClose}
            sx={{ zIndex: 1400 }}
            slotProps={{ backdrop: { sx: { backgroundColor: 'transparent' } } }}
        >
            <Box
                sx={{
                    position: 'fixed',
                    inset: 0,
                    backgroundColor: 'rgba(7,11,28,0.98)',
                    display: 'flex',
                    alignItems: 'center',
                    overflowX: 'hidden',
                    overflowY: 'auto',
                    outline: 'none',
                }}
            >
                <AnimatePresence mode="popLayout" custom={direction}>
                    {art && (
                        <motion.div
                            key={art.id}
                            custom={direction}
                            variants={{
                                initial: (dir: number) => ({ x: dir > 0 ? '100%' : '-100%', opacity: 0 }),
                                animate: { x: 0, opacity: 1 },
                                exit: (dir: number) => ({ x: dir > 0 ? '-100%' : '100%', opacity: 0 }),
                            }}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                            transition={{ duration: 0.35, ease: 'easeInOut' }}
                            style={{ width: '100%' }}
                            drag="x"
                            dragConstraints={{ left: 0, right: 0 }}
                            dragElastic={0.1}
                            onDragEnd={(_e, info) => {
                                if (info.offset.x < -50 && hasNext) navigateNext();
                                else if (info.offset.x > 50 && hasPrev) navigatePrev();
                            }}
                        >
                            {/* Card — position:relative so absolute arrows are anchored here */}
                            <Box
                                sx={{
                                    position: 'relative',
                                    display: 'flex',
                                    flexDirection: { xs: 'column', md: 'row' },
                                    alignItems: { xs: 'flex-start', md: 'center' },
                                    minHeight: '100vh',
                                    px: { xs: 3, md: 7 },
                                    py: { xs: 10, md: 4 },
                                    gap: { xs: 3, md: 6 },
                                }}
                            >
                                {/* Desktop nav arrows — absolute at card edges */}
                                {hasPrev && (
                                    <IconButton
                                        onClick={navigatePrev}
                                        sx={{
                                            display: { xs: 'none', md: 'flex' },
                                            position: 'absolute',
                                            left: 8,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            color: '#9e9e9e',
                                            '&:hover': { color: '#f0ede8' },
                                            zIndex: 2,
                                        }}
                                    >
                                        <ArrowBackIosNewIcon />
                                    </IconButton>
                                )}
                                {hasNext && (
                                    <IconButton
                                        onClick={navigateNext}
                                        sx={{
                                            display: { xs: 'none', md: 'flex' },
                                            position: 'absolute',
                                            right: 8,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            color: '#9e9e9e',
                                            '&:hover': { color: '#f0ede8' },
                                            zIndex: 2,
                                        }}
                                    >
                                        <ArrowForwardIosIcon />
                                    </IconButton>
                                )}

                                {/* Image side */}
                                <Box
                                    sx={{
                                        flex: { xs: '1 1 auto', md: '0 0 60%' },
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Box sx={{ outline: '1px solid #c9a84c', outlineOffset: '8px' }}>
                                        <img
                                            src={art.image}
                                            alt={art.title}
                                            style={{
                                                maxHeight: '85vh',
                                                maxWidth: '100%',
                                                objectFit: 'contain',
                                                display: 'block',
                                            }}
                                        />
                                    </Box>
                                </Box>

                                {/* Metadata side */}
                                <Box sx={{ flex: { xs: '1 1 auto', md: '0 0 35%' } }}>
                                    <Box sx={{ width: 32, height: 2, backgroundColor: '#c9a84c', mb: 3 }} />
                                    <Typography
                                        variant="h3"
                                        sx={{ mb: 1, fontSize: { xs: '1.8rem', md: '2.5rem' }, lineHeight: 1.2 }}
                                    >
                                        {art.title}
                                    </Typography>
                                    {(art.medium || art.date) && (
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: '#9e9e9e',
                                                mb: 3,
                                                fontFamily: '"Inter", sans-serif',
                                                fontSize: '0.8rem',
                                            }}
                                        >
                                            {[art.medium, art.date].filter(Boolean).join(' · ')}
                                        </Typography>
                                    )}
                                    {getDescription(art, activeTone) && (
                                        <Typography
                                            data-testid="art-description"
                                            variant="body1"
                                            sx={{
                                                color: '#c8c4be',
                                                lineHeight: 1.8,
                                                fontFamily: '"Inter", sans-serif',
                                                fontSize: '0.9rem',
                                            }}
                                        >
                                            {getDescription(art, activeTone)}
                                        </Typography>
                                    )}

                                    {/* Mobile nav arrows — inline row below metadata */}
                                    <Box sx={{ display: { xs: 'flex', md: 'none' }, gap: 1, mt: 3 }}>
                                        {hasPrev && (
                                            <IconButton
                                                onClick={navigatePrev}
                                                sx={{ color: '#9e9e9e', '&:hover': { color: '#f0ede8' } }}
                                            >
                                                <ArrowBackIosNewIcon />
                                            </IconButton>
                                        )}
                                        {hasNext && (
                                            <IconButton
                                                onClick={navigateNext}
                                                sx={{ color: '#9e9e9e', '&:hover': { color: '#f0ede8' } }}
                                            >
                                                <ArrowForwardIosIcon />
                                            </IconButton>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Close button */}
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: 'fixed',
                        top: 16,
                        right: 16,
                        color: '#9e9e9e',
                        '&:hover': { color: '#f0ede8' },
                        zIndex: 3,
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>
        </Modal>
    );
};

export default ArtDetailModal;
